import type { App } from 'vue';
import axios, { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosResponse } from 'axios';
import useUserStore from '@/store/user.store';
import { ApiCodeEnum } from '@/const';
import { handleErrorResponse, handleNetworkError } from '@/api/api.utils';
import { storeToRefs } from 'pinia';
import createAdmApiPlugin from '@/api/adm.api.plugin';
import { createAdminApi, type ApiContext } from '@/api/adm-api';
import type { UserItem } from '@/types/api/account.type';

// API 响应接口
export interface ApiResponse<T = any> {
  code: number;
  data: T;
  message: string;
}

function getTokenAction() {
  const store = useUserStore();
  const { token, userInfo } = storeToRefs(store);

  return {
    token: token.value,
    userId: (userInfo.value as UserItem)?.id ?? ''
  };
}

/**
 * 获取 API 地址
 * 开发环境默认使用测试服务器
 */
function getApiUrl(): string {
  // 生产环境根据域名判断
  const _host = window.location.host;

  // 可以根据需要添加更多环境判断，例如：
  // if (_host.includes('xxx.com')) {
  //   return 'https://xxx-api.com';
  // }

  // 默认返回环境变量配置的地址，或测试环境
  return import.meta.env.VITE_API_BASE_URL || 'https://mgt-api-dev.001thebest.com';
}

// 创建 axios 实例
const service: AxiosInstance = axios.create({
  baseURL: getApiUrl(),
  timeout: 100000
});

// 请求拦截器
service.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 添加 token 和 userId
    const { token, userId } = getTokenAction();

    if (token) {
    //   if (!config.headers) {
    //     config.headers = {};
    //   }

      config.headers['x-token'] = token;
      config.headers['x-user-id'] = userId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
service.interceptors.response.use(
  (response: AxiosResponse) => {
    const { data } = response;

    if (typeof data.code === 'undefined' || data.code === ApiCodeEnum.Success) {
      return response;
    } else {
      return handleErrorResponse(response);
    }
  },
  (error) => {
    return handleNetworkError(error);
  }
);

// 创建全局 API 实例（可以在任何地方使用，包括 store）
export const appApi: ApiContext = createAdminApi(service);

// 导出 axios 实例（用于直接调用）
export { service as axiosInstance };

/**
 * API 插件
 * @param app Vue应用实例
 */
export function setupAdmApiPlugin(app: App) {
  app.use(createAdmApiPlugin(service));
}